import {
  useId,
  useState,
  type ComponentPropsWithoutRef,
  type ReactNode,
} from "react";
import {
  DayPicker,
  getDefaultClassNames,
  type ChevronProps,
  type DateRange as DayPickerDateRange,
  type DayButtonProps,
  type Matcher,
} from "react-day-picker";

import { focusRing } from "../../foundations/a11y";
import { Outline } from "../../icons";
import { cn } from "../../lib/cn";
import { Button } from "../Button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  useNearestDialogPortalContainer,
} from "../Popover";

export type DateRangeValue = {
  start?: string;
  end?: string;
};

export type DatePreset = {
  label: string;
  value: string;
};

export type DateRangePreset = {
  label: string;
  value: DateRangeValue;
};

type DatePickerBaseProps = {
  label: string;
  helperText?: string;
  error?: string;
  clearLabel?: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  min?: string;
  max?: string;
  numberOfMonths?: number;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
  labelClassName?: string;
  triggerClassName?: string;
  clearButtonClassName?: string;
  iconClassName?: string;
  showClearButton?: boolean;
} & Omit<ComponentPropsWithoutRef<"div">, "onChange" | "value">;

export type DatePickerSingleProps = DatePickerBaseProps & {
  mode?: "single";
  value?: string;
  onChange: (value?: string) => void;
  presets?: DatePreset[];
  name?: string;
};

export type DatePickerRangeProps = DatePickerBaseProps & {
  mode: "range";
  value: DateRangeValue;
  onChange: (value: DateRangeValue) => void;
  presets?: DateRangePreset[];
  startName?: string;
  endName?: string;
};

export type DatePickerProps = DatePickerSingleProps | DatePickerRangeProps;

const displayDateFormatter = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
});

function compactRange(value: DateRangeValue): DateRangeValue {
  const next: DateRangeValue = {};
  if (value.start) next.start = value.start;
  if (value.end) next.end = value.end;
  return next;
}

function rangesMatch(left: DateRangeValue, right: DateRangeValue): boolean {
  return (
    (left.start ?? "") === (right.start ?? "") &&
    (left.end ?? "") === (right.end ?? "")
  );
}

function parseIsoDate(value?: string): Date | undefined {
  if (!value) return undefined;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return undefined;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);

  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return undefined;
  }

  return date;
}

function toIsoDate(date?: Date): string | undefined {
  if (!date) return undefined;

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatDisplayDate(value?: string, fallback = "Select date"): string {
  const date = parseIsoDate(value);
  return date ? displayDateFormatter.format(date) : fallback;
}

function formatDateForLabel(value: string | undefined, placeholder: string) {
  return value ? formatDisplayDate(value, placeholder) : placeholder;
}

function formatRangeForLabel(value: DateRangeValue, placeholder: string) {
  if (value.start && value.end) {
    return `${formatDisplayDate(value.start)} - ${formatDisplayDate(value.end)}`;
  }

  if (value.start) {
    return `${formatDisplayDate(value.start)} - Select end`;
  }

  if (value.end) {
    return `Select start - ${formatDisplayDate(value.end)}`;
  }

  return placeholder;
}

function toDayPickerRange(
  value: DateRangeValue,
): DayPickerDateRange | undefined {
  const from = parseIsoDate(value.start);
  const to = parseIsoDate(value.end);

  if (!from && !to) return undefined;
  return { from, to };
}

function toDateRangeValue(range?: DayPickerDateRange): DateRangeValue {
  return compactRange({
    start: toIsoDate(range?.from),
    end: toIsoDate(range?.to),
  });
}

function getDisabledMatchers(minDate?: Date, maxDate?: Date): Matcher[] {
  const matchers: Matcher[] = [];
  if (minDate) matchers.push({ before: minDate });
  if (maxDate) matchers.push({ after: maxDate });
  return matchers;
}

function usePopoverOpenState({
  open,
  defaultOpen,
  onOpenChange,
}: {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(
    defaultOpen ?? false,
  );
  const isControlled = open !== undefined;
  const currentOpen = isControlled ? open : uncontrolledOpen;

  function setOpen(nextOpen: boolean) {
    if (!isControlled) {
      setUncontrolledOpen(nextOpen);
    }

    onOpenChange?.(nextOpen);
  }

  return [currentOpen, setOpen] as const;
}

function compareCalendarDates(left: Date, right: Date): number {
  return (
    Date.UTC(left.getFullYear(), left.getMonth(), left.getDate()) -
    Date.UTC(right.getFullYear(), right.getMonth(), right.getDate())
  );
}

function isSameCalendarDate(left: Date, right: Date): boolean {
  return compareCalendarDates(left, right) === 0;
}

export function DatePicker(props: DatePickerProps) {
  if (props.mode === "range") {
    return <DateRangePickerField {...props} />;
  }

  return <SingleDatePickerField {...props} />;
}

function SingleDatePickerField({
  mode: _mode,
  label,
  value,
  onChange,
  helperText,
  error,
  presets = [],
  clearLabel = "Clear date",
  placeholder = "Select date",
  disabled = false,
  required = false,
  min,
  max,
  name,
  numberOfMonths = 1,
  open,
  defaultOpen,
  onOpenChange,
  className,
  labelClassName,
  triggerClassName,
  clearButtonClassName,
  iconClassName,
  showClearButton = true,
  id,
  ...rest
}: DatePickerSingleProps) {
  void _mode;

  const minDate = parseIsoDate(min);
  const maxDate = parseIsoDate(max);
  const selectedDate = parseIsoDate(value);
  const dateText = formatDateForLabel(value, placeholder);
  const hasValue = Boolean(value);
  const initialMonth = selectedDate ?? minDate;
  const [isOpen, setIsOpen] = usePopoverOpenState({
    open,
    defaultOpen,
    onOpenChange,
  });

  return (
    <DatePickerField
      id={id}
      label={label}
      helperText={helperText}
      error={error}
      clearLabel={clearLabel}
      disabled={disabled}
      required={required}
      open={isOpen}
      onOpenChange={setIsOpen}
      className={className}
      labelClassName={labelClassName}
      triggerClassName={triggerClassName}
      clearButtonClassName={clearButtonClassName}
      iconClassName={iconClassName}
      showClearButton={showClearButton}
      hasValue={hasValue}
      valueText={dateText}
      calendar={
        <SingleDateCalendar
          selected={selectedDate}
          onSelect={(date) => onChange(toIsoDate(date))}
          defaultMonth={initialMonth}
          minDate={minDate}
          maxDate={maxDate}
          numberOfMonths={numberOfMonths}
        />
      }
      hiddenFields={
        name ? <input type="hidden" name={name} value={value ?? ""} /> : null
      }
      presets={
        <DatePresets
          label={label}
          value={value}
          presets={presets}
          disabled={disabled}
          onChange={onChange}
        />
      }
      onClear={() => onChange(undefined)}
      {...rest}
    />
  );
}

function DateRangePickerField({
  mode: _mode,
  label,
  value,
  onChange,
  helperText,
  error,
  presets = [],
  clearLabel = "Clear dates",
  placeholder = "Select dates",
  disabled = false,
  required = false,
  min,
  max,
  startName,
  endName,
  numberOfMonths = 2,
  open,
  defaultOpen,
  onOpenChange,
  className,
  labelClassName,
  triggerClassName,
  clearButtonClassName,
  iconClassName,
  showClearButton = true,
  id,
  ...rest
}: DatePickerRangeProps) {
  void _mode;

  const minDate = parseIsoDate(min);
  const maxDate = parseIsoDate(max);
  const selectedRange = toDayPickerRange(value);
  const rangeText = formatRangeForLabel(value, placeholder);
  const hasValue = Boolean(value.start || value.end);
  const initialMonth = selectedRange?.from ?? selectedRange?.to ?? minDate;
  const [isOpen, setIsOpen] = usePopoverOpenState({
    open,
    defaultOpen,
    onOpenChange,
  });

  function handleSelect(range?: DayPickerDateRange) {
    const nextValue = toDateRangeValue(range);

    onChange(nextValue);

    if (nextValue.start && nextValue.end) {
      setIsOpen(false);
    }
  }

  return (
    <DatePickerField
      id={id}
      label={label}
      helperText={helperText}
      error={error}
      clearLabel={clearLabel}
      disabled={disabled}
      required={required}
      open={isOpen}
      onOpenChange={setIsOpen}
      className={className}
      labelClassName={labelClassName}
      triggerClassName={triggerClassName}
      clearButtonClassName={clearButtonClassName}
      iconClassName={iconClassName}
      showClearButton={showClearButton}
      hasValue={hasValue}
      valueText={rangeText}
      calendar={
        <DateRangeCalendar
          selected={selectedRange}
          onSelect={handleSelect}
          defaultMonth={initialMonth}
          minDate={minDate}
          maxDate={maxDate}
          numberOfMonths={numberOfMonths}
        />
      }
      hiddenFields={
        <>
          {startName ? (
            <input type="hidden" name={startName} value={value.start ?? ""} />
          ) : null}
          {endName ? (
            <input type="hidden" name={endName} value={value.end ?? ""} />
          ) : null}
        </>
      }
      presets={
        <DateRangePresets
          label={label}
          value={value}
          presets={presets}
          disabled={disabled}
          onChange={onChange}
        />
      }
      onClear={() => onChange({})}
      {...rest}
    />
  );
}

type DatePickerFieldProps = {
  label: string;
  helperText?: string;
  error?: string;
  clearLabel: string;
  disabled: boolean;
  required: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
  labelClassName?: string;
  triggerClassName?: string;
  clearButtonClassName?: string;
  iconClassName?: string;
  showClearButton: boolean;
  hasValue: boolean;
  valueText: string;
  calendar: ReactNode;
  hiddenFields?: ReactNode;
  presets?: ReactNode;
  onClear: () => void;
} & Omit<ComponentPropsWithoutRef<"div">, "onChange" | "value">;

function DatePickerField({
  label,
  helperText,
  error,
  clearLabel,
  disabled,
  required,
  open,
  onOpenChange,
  className,
  labelClassName,
  triggerClassName,
  clearButtonClassName,
  iconClassName,
  showClearButton,
  id,
  hasValue,
  valueText,
  calendar,
  hiddenFields,
  presets,
  onClear,
  ...rest
}: DatePickerFieldProps) {
  const autoId = useId();
  const fieldId = id ?? autoId;
  const labelId = `${fieldId}-label`;
  const [fieldRef, portalContainer] =
    useNearestDialogPortalContainer<HTMLDivElement>();
  const messageId = error
    ? `${fieldId}-error`
    : helperText
      ? `${fieldId}-help`
      : undefined;
  const triggerLabel = `${label}${required ? " (required)" : ""}: ${valueText}`;

  return (
    <div
      ref={fieldRef}
      id={fieldId}
      role="group"
      aria-labelledby={labelId}
      aria-describedby={messageId}
      aria-disabled={disabled ? true : undefined}
      data-required={required ? true : undefined}
      className={cn("flex flex-col gap-1.5", className)}
      {...rest}
    >
      <div className="flex items-center gap-3">
        <span
          id={labelId}
          className={cn("text-label text-text", labelClassName)}
        >
          {label}
          {required && (
            <span className="text-error-text" aria-hidden="true">
              {" *"}
            </span>
          )}
        </span>
      </div>

      <div className="flex max-w-full items-center gap-2">
        <Popover open={open} onOpenChange={onOpenChange}>
          <PopoverTrigger asChild>
            <button
              type="button"
              disabled={disabled}
              aria-label={triggerLabel}
              aria-describedby={messageId}
              className={cn(
                "inline-flex h-10 w-full min-w-0 items-center justify-start gap-2 rounded-md border border-separator bg-surface px-3 py-2 text-left text-body text-text transition-colors duration-base ease-standard hover:bg-fill",
                "disabled:cursor-not-allowed disabled:opacity-50",
                focusRing,
                error && "ring-1 ring-inset ring-error",
                triggerClassName,
              )}
            >
              <Outline.CalendarDaysIcon
                className={cn(
                  "text-text-secondary size-5 shrink-0",
                  iconClassName,
                )}
                aria-hidden="true"
              />
              <span className={cn("truncate", !hasValue && "text-placeholder")}>
                {valueText}
              </span>
            </button>
          </PopoverTrigger>
          <PopoverContent
            aria-label={`${label} calendar`}
            align="start"
            sideOffset={8}
            portalContainer={portalContainer ?? undefined}
            className="border-separator max-w-[calc(100vw-2rem)] overflow-x-auto border"
          >
            {calendar}
          </PopoverContent>
        </Popover>

        {hasValue && showClearButton && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={disabled}
            aria-label={clearLabel}
            onClick={onClear}
            className={cn(
              "text-text-tertiary hover:text-text size-8 shrink-0 px-0",
              clearButtonClassName,
            )}
            leftIcon={
              <Outline.XMarkIcon className="size-4" aria-hidden="true" />
            }
          />
        )}
      </div>

      {hiddenFields}
      {presets}

      {error ? (
        <p id={`${fieldId}-error`} className="text-caption text-error-text">
          {error}
        </p>
      ) : helperText ? (
        <p id={`${fieldId}-help`} className="text-caption text-text-secondary">
          {helperText}
        </p>
      ) : null}
    </div>
  );
}

type DatePresetsProps = {
  label: string;
  value?: string;
  presets: DatePreset[];
  disabled: boolean;
  onChange: (value?: string) => void;
};

function DatePresets({
  label,
  value,
  presets,
  disabled,
  onChange,
}: DatePresetsProps) {
  if (presets.length === 0) return null;

  return (
    <div
      role="group"
      className="flex flex-wrap gap-2"
      aria-label={`${label} presets`}
    >
      {presets.map((preset) => {
        const selected = value === preset.value;
        const presetKey = [preset.label, preset.value].join(":");

        return (
          <PresetButton
            key={presetKey}
            selected={selected}
            disabled={disabled}
            onClick={() => onChange(preset.value)}
          >
            {preset.label}
          </PresetButton>
        );
      })}
    </div>
  );
}

type DateRangePresetsProps = {
  label: string;
  value: DateRangeValue;
  presets: DateRangePreset[];
  disabled: boolean;
  onChange: (value: DateRangeValue) => void;
};

function DateRangePresets({
  label,
  value,
  presets,
  disabled,
  onChange,
}: DateRangePresetsProps) {
  if (presets.length === 0) return null;

  return (
    <div
      role="group"
      className="flex flex-wrap gap-2"
      aria-label={`${label} presets`}
    >
      {presets.map((preset) => {
        const selected = rangesMatch(value, preset.value);
        const presetKey = [
          preset.label,
          preset.value.start ?? "",
          preset.value.end ?? "",
        ].join(":");

        return (
          <PresetButton
            key={presetKey}
            selected={selected}
            disabled={disabled}
            onClick={() => onChange(compactRange(preset.value))}
          >
            {preset.label}
          </PresetButton>
        );
      })}
    </div>
  );
}

type PresetButtonProps = {
  selected: boolean;
  disabled: boolean;
  onClick: () => void;
  children: ReactNode;
};

function PresetButton({
  selected,
  disabled,
  onClick,
  children,
}: PresetButtonProps) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "inline-flex h-8 items-center rounded-full px-3 text-label transition-colors duration-base ease-standard",
        "disabled:pointer-events-none disabled:opacity-50",
        selected
          ? "bg-accent-wash text-accent"
          : "bg-fill text-text-secondary hover:bg-fill-secondary hover:text-text",
        focusRing,
      )}
    >
      {children}
    </button>
  );
}

type SingleDateCalendarProps = {
  selected?: Date;
  onSelect: (date?: Date) => void;
  defaultMonth?: Date;
  minDate?: Date;
  maxDate?: Date;
  numberOfMonths: number;
};

function SingleDateCalendar({
  selected,
  onSelect,
  defaultMonth,
  minDate,
  maxDate,
  numberOfMonths,
}: SingleDateCalendarProps) {
  const defaultClassNames = getDefaultClassNames();
  const disabledMatchers = getDisabledMatchers(minDate, maxDate);

  return (
    <DayPicker
      mode="single"
      selected={selected}
      onSelect={onSelect}
      defaultMonth={defaultMonth}
      startMonth={minDate}
      endMonth={maxDate}
      numberOfMonths={numberOfMonths}
      showOutsideDays
      pagedNavigation={numberOfMonths > 1}
      disabled={disabledMatchers.length > 0 ? disabledMatchers : undefined}
      className={cn("w-fit", defaultClassNames.root)}
      classNames={getCalendarClassNames(defaultClassNames)}
      components={calendarComponents}
    />
  );
}

type DateRangeCalendarProps = {
  selected?: DayPickerDateRange;
  onSelect: (range?: DayPickerDateRange) => void;
  defaultMonth?: Date;
  minDate?: Date;
  maxDate?: Date;
  numberOfMonths: number;
};

function DateRangeCalendar({
  selected,
  onSelect,
  defaultMonth,
  minDate,
  maxDate,
  numberOfMonths,
}: DateRangeCalendarProps) {
  const [previewDate, setPreviewDate] = useState<Date>();
  const defaultClassNames = getDefaultClassNames();
  const disabledMatchers = getDisabledMatchers(minDate, maxDate);
  const canPreviewRange = Boolean(selected?.from && !selected.to);
  const previewModifiers = getPendingRangePreviewModifiers(
    selected,
    previewDate,
  );

  function handleSelect(range: DayPickerDateRange | undefined) {
    setPreviewDate(undefined);

    if (
      !selected?.from &&
      range?.from &&
      range.to &&
      isSameCalendarDate(range.from, range.to)
    ) {
      onSelect({ from: range.from, to: undefined });
      return;
    }

    onSelect(range);
  }

  return (
    <DayPicker
      mode="range"
      selected={selected}
      onSelect={handleSelect}
      defaultMonth={defaultMonth}
      startMonth={minDate}
      endMonth={maxDate}
      numberOfMonths={numberOfMonths}
      showOutsideDays
      pagedNavigation
      excludeDisabled
      disabled={disabledMatchers.length > 0 ? disabledMatchers : undefined}
      modifiers={previewModifiers}
      onDayMouseEnter={(date, modifiers) => {
        if (!canPreviewRange || modifiers.disabled || modifiers.hidden) {
          setPreviewDate(undefined);
          return;
        }

        setPreviewDate(date);
      }}
      onDayMouseLeave={() => setPreviewDate(undefined)}
      className={cn("w-fit", defaultClassNames.root)}
      classNames={getCalendarClassNames(defaultClassNames)}
      components={calendarComponents}
    />
  );
}

function getPendingRangePreviewModifiers(
  selected: DayPickerDateRange | undefined,
  previewDate: Date | undefined,
): Record<string, Matcher | Matcher[] | undefined> | undefined {
  const selectedStart = selected?.from;

  if (
    !selectedStart ||
    selected?.to ||
    !previewDate ||
    isSameCalendarDate(selectedStart, previewDate)
  ) {
    return undefined;
  }

  const [rangeStart, rangeEnd] =
    compareCalendarDates(selectedStart, previewDate) <= 0
      ? [selectedStart, previewDate]
      : [previewDate, selectedStart];

  return {
    preview_range_start: rangeStart,
    preview_range_middle: (date: Date) =>
      compareCalendarDates(date, rangeStart) > 0 &&
      compareCalendarDates(date, rangeEnd) < 0,
    preview_range_end: rangeEnd,
  };
}

function getCalendarClassNames(
  defaultClassNames: ReturnType<typeof getDefaultClassNames>,
) {
  return {
    months: cn(
      "relative flex flex-col gap-6 md:flex-row",
      defaultClassNames.months,
    ),
    month: cn("flex w-full flex-col gap-3", defaultClassNames.month),
    nav: cn(
      "absolute inset-x-0 top-0 flex items-center justify-between gap-1",
      defaultClassNames.nav,
    ),
    button_previous: cn(
      calendarNavButtonClassName,
      defaultClassNames.button_previous,
    ),
    button_next: cn(calendarNavButtonClassName, defaultClassNames.button_next),
    month_caption: cn(
      "flex h-9 items-center justify-center px-10",
      defaultClassNames.month_caption,
    ),
    caption_label: cn(
      "text-label font-medium text-text",
      defaultClassNames.caption_label,
    ),
    month_grid: cn("w-full border-collapse", defaultClassNames.month_grid),
    weekdays: cn("flex", defaultClassNames.weekdays),
    weekday: cn(
      "flex size-9 items-center justify-center text-caption font-normal text-text-secondary",
      defaultClassNames.weekday,
    ),
    week: cn("mt-1 flex w-full", defaultClassNames.week),
    day: cn("relative size-9 p-0 text-center", defaultClassNames.day),
    selected: cn(defaultClassNames.selected),
    range_start: cn(
      "rounded-l-md bg-accent-wash",
      defaultClassNames.range_start,
    ),
    range_middle: cn(
      "rounded-none bg-accent-wash",
      defaultClassNames.range_middle,
    ),
    range_end: cn("rounded-r-md bg-accent-wash", defaultClassNames.range_end),
    today: cn(defaultClassNames.today),
    outside: cn("text-text-tertiary", defaultClassNames.outside),
    disabled: cn(
      "pointer-events-none text-text-tertiary opacity-40",
      defaultClassNames.disabled,
    ),
    hidden: cn("invisible", defaultClassNames.hidden),
  };
}

const calendarNavButtonClassName = cn(
  "inline-flex size-8 items-center justify-center rounded-md text-text-secondary transition-colors duration-base ease-standard hover:bg-fill hover:text-text",
  "disabled:pointer-events-none disabled:opacity-50",
  focusRing,
);

const calendarComponents = {
  Chevron: CalendarChevron,
  DayButton: CalendarDayButton,
};

function CalendarChevron({ className, orientation, ...props }: ChevronProps) {
  const Icon =
    orientation === "left"
      ? Outline.ChevronLeftIcon
      : orientation === "right"
        ? Outline.ChevronRightIcon
        : Outline.ChevronDownIcon;

  return (
    <Icon className={cn("size-4", className)} aria-hidden="true" {...props} />
  );
}

function CalendarDayButton({
  className,
  day,
  modifiers,
  ...props
}: DayButtonProps) {
  const rangeStart = modifiers.range_start || modifiers.preview_range_start;
  const rangeEnd = modifiers.range_end || modifiers.preview_range_end;
  const rangeMiddle = modifiers.range_middle || modifiers.preview_range_middle;
  const selectedSingle =
    modifiers.selected && !rangeStart && !rangeEnd && !rangeMiddle;

  return (
    <button
      data-day={toIsoDate(day.date)}
      data-selected-single={selectedSingle || undefined}
      data-range-start={rangeStart || undefined}
      data-range-end={rangeEnd || undefined}
      data-range-middle={rangeMiddle || undefined}
      className={cn(
        "flex size-9 items-center justify-center rounded-md text-label font-normal leading-none text-text transition-colors duration-base ease-standard hover:bg-fill-secondary",
        "disabled:pointer-events-none disabled:opacity-40",
        "data-[range-middle=true]:rounded-none data-[range-middle=true]:bg-accent-wash data-[range-middle=true]:text-text data-[range-middle=true]:hover:bg-accent-wash",
        "data-[range-start=true]:bg-accent data-[range-start=true]:text-on-accent",
        "data-[range-end=true]:bg-accent data-[range-end=true]:text-on-accent",
        "data-[selected-single=true]:bg-accent data-[selected-single=true]:text-on-accent",
        modifiers.today && !modifiers.selected && "font-semibold text-accent",
        modifiers.outside && "text-text-tertiary",
        focusRing,
        className,
      )}
      {...props}
    />
  );
}
